/**
 * @file LogoutModal.tsx
 *
 * @description This component presents a confirmation dialog when a user attempts to log out,
 * providing an opportunity to cancel the logout operation or confirm it.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Fade,
  CircularProgress,
  Portal,
} from "@mui/material";
import { keyframes } from "@mui/system";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import { useErrorSnackbar } from "../shared/hooks/useErrorSnackbar";
import { SnackBarMessage } from "./SnackBarMessage";

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

/**
 * Props interface for the LogoutModal component.
 * @interface LogoutModalProps
 * @property {boolean} open - Controls the visibility of the modal dialog
 * @property {() => void} onClose - Callback function to be executed when the modal is closed without logging out
 * @property {() => void} onLogout - Callback function to be executed when the user confirms logout
 */
interface LogoutModalProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

/**
 * @name LogoutModal
 *
 * @description A modal dialog component that asks for confirmation before logging out a user.
 *
 * @param {LogoutModalProps} props - Component properties
 * @param {boolean} props.open - Controls the visibility of the modal
 * @param {() => void} props.onClose - Handler for canceling the logout action and closing the modal
 * @param {() => void} props.onLogout - Handler for executing the logout action
 *
 * @returns {JSX.Element} A confirmation dialog for user logout
 */
const LogoutModal: React.FC<LogoutModalProps> = ({
  open,
  onClose,
  onLogout,
}) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { showError, snackBarMessage, snackBarMode, handleCloseSnackbar } =
    useErrorSnackbar();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    onClose();
    try {
      await onLogout();
    } catch (error) {
      setIsLoggingOut(false);
      console.error("Logout failed:", error);
      showError("Sign out failed. Please try again.");
    }
  };

  return (
    <>
      <Portal>
        <Fade in={isLoggingOut} timeout={300} unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              backdropFilter: "blur(8px)",
              backgroundColor: "background.default",
            }}
          >
            <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  border: (t) => `3px solid ${t.palette.divider}`,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <CircularProgress
                size={48}
                sx={{ color: "primary.main", position: "absolute" }}
              />
            </Box>
            <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
              Signing you out...
            </Typography>
          </Box>
        </Fade>
      </Portal>

    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="logout-dialog-title"
      aria-describedby="logout-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 420,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: "0 4px 14px rgba(211, 47, 47, 0.3)",
          }}
        >
          <LogoutIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>

        <Typography
          id="logout-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          Sign Out?
        </Typography>

        <Typography
          id="logout-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          Are you sure you want to sign out? You'll need to log in again to
          access your account.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": {
            marginLeft: 0,
          },
        }}
      >
        <Button
          onClick={handleLogout}
          color="error"
          variant="contained"
          fullWidth
          disableElevation
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "error.dark",
            },
          }}
          autoFocus
        >
          Sign Out
        </Button>
        <Button
          onClick={onClose}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>

      <SnackBarMessage
        message={snackBarMessage}
        mode={snackBarMode}
        handleClose={handleCloseSnackbar}
        horizontalAnchor="center"
        verticalAnchor="top"
      />
    </>
  );
};

export default LogoutModal;
